import type { LabelProps } from 'recharts';
import { FONTE_ROTULO } from './chartTheme';

/**
 * Rótulos de valor dos gráficos dos painéis (26/10/2026: o número fica visível, sem passar o
 * mouse). O texto sai na cor do texto, nunca na cor da série, e numa linha só: o rótulo padrão
 * do `LabelList` quebra o texto na largura da barra ("R$" em cima, "8 mi" embaixo).
 */

const numero = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Início e fim de uma medida que o recharts pode mandar negativa (barra de valor negativo). */
const faixa = (inicio: number, tamanho: unknown): [number, number] => {
  const fim = inicio + (numero(tamanho) ?? 0);
  return [Math.min(inicio, fim), Math.max(inicio, fim)];
};

/** Segunda linha opcional do rótulo da coluna (ex.: o desvio em cima do produzido). */
export interface LinhaExtra {
  texto: (indice: number) => string | null;
  cor: string;
}

/**
 * Coluna (barra vertical): valor em cima; valor negativo fica embaixo da barra. Com `extra`, uma
 * segunda linha menor vai acima do valor (abaixo, na barra negativa).
 *
 * `emPe` (03/11/2026): o número girado 90°, subindo a partir do topo da coluna (na negativa, da
 * linha do zero), com o extra na mesma linha. É o modo de quando o número deitado não cabe na
 * largura da coluna: o gráfico precisa de margem em cima (`margemDoRotuloEmPe`).
 */
export function rotuloNoTopo(
  formatar: (v: number, indice: number) => string,
  cor: string,
  extra?: LinhaExtra,
  emPe = false,
) {
  const RotuloNoTopo = (props: LabelProps & { index?: number }) => {
    const { x, y, width, height, value, index } = props;
    const v = numero(value);
    if (v === null || typeof x !== 'number' || typeof y !== 'number') return null;
    const [topo, base] = faixa(y, height);
    const centro = x + (numero(width) ?? 0) / 2;
    const i = index ?? 0;
    const linhaExtra = extraDo(extra, i);
    if (emPe) return textoEmPe(centro, topo - 6, formatar(v, i), cor, linhaExtra);
    // Negativo: o texto desce (abaixo da base); positivo: sobe (acima do topo).
    const passo = v < 0 ? FONTE_ROTULO + 2 : -(FONTE_ROTULO + 2);
    const yTexto = v < 0 ? base + FONTE_ROTULO + 4 : topo - 6;
    return (
      <g>
        <text x={centro} y={yTexto} textAnchor="middle" fill={cor} fontSize={FONTE_ROTULO} fontWeight={600}>
          {formatar(v, i)}
        </text>
        {linhaExtra && textoExtra(centro, yTexto + passo, linhaExtra.texto, linhaExtra.cor)}
      </g>
    );
  };
  return RotuloNoTopo;
}

/** O texto da segunda linha (ex.: o desvio) para a coluna `i`, ou nada. */
const extraDo = (extra: LinhaExtra | undefined, i: number): { texto: string; cor: string } | null => {
  const texto = extra?.texto(i);
  return extra && texto ? { texto, cor: extra.cor } : null;
};

/** Texto girado 90°, crescendo para cima a partir de (x, y); o extra vai na mesma linha, mais leve. */
const textoEmPe = (x: number, y: number, texto: string, cor: string, extra?: { texto: string; cor: string } | null) => (
  <text
    x={x}
    y={y}
    transform={`rotate(-90 ${x} ${y})`}
    textAnchor="start"
    dominantBaseline="central"
    fill={cor}
    fontSize={FONTE_ROTULO}
    fontWeight={600}
  >
    {texto}
    {extra ? (
      <tspan fill={extra.cor} fontSize={FONTE_ROTULO - 1} fontWeight={400}>
        {`  ${extra.texto}`}
      </tspan>
    ) : null}
  </text>
);

const textoExtra = (x: number, y: number, texto: string | null, cor: string) =>
  texto ? (
    <text x={x} y={y} textAnchor="middle" fill={cor} fontSize={FONTE_ROTULO - 1}>
      {texto}
    </text>
  ) : null;

/** Barra horizontal: valor depois da ponta; valor negativo, antes do começo da barra. */
export function rotuloNaPonta(formatar: (v: number) => string, cor: string) {
  const RotuloNaPonta = (props: LabelProps) => {
    const { x, y, width, height, value } = props;
    const v = numero(value);
    if (v === null || typeof x !== 'number' || typeof y !== 'number') return null;
    const [inicio, fim] = faixa(x, width);
    const meio = y + (numero(height) ?? 0) / 2;
    const negativo = v < 0;
    return (
      <text
        x={negativo ? inicio - 6 : fim + 6}
        y={meio}
        dominantBaseline="central"
        textAnchor={negativo ? 'end' : 'start'}
        fill={cor}
        fontSize={FONTE_ROTULO}
        fontWeight={600}
      >
        {formatar(v)}
      </text>
    );
  };
  return RotuloNaPonta;
}

/**
 * Ponto de linha: valor acima do ponto, só nos índices que `mostrar` aceita (o último dia e os
 * extremos, ou todos com "Mostrar valores"). Número em todo ponto de 30 dias × 3 séries vira
 * uma sopa que ninguém lê.
 */
export function rotuloDoPonto(
  formatar: (v: number, indice: number) => string,
  cor: string,
  mostrar: (indice: number) => boolean,
  posicao: PosicaoPonto = {},
) {
  const RotuloDoPonto = (props: LabelProps & { index?: number }) => {
    const { x, y, value, index } = props;
    const v = numero(value);
    if (v === null || typeof x !== 'number' || typeof y !== 'number' || index === undefined || !mostrar(index)) return null;
    if (posicao.emPe) return textoEmPe(x, y - 8, formatar(v, index), cor);
    return (
      <text {...lugarDoTexto(x, y, index, posicao)} fill={cor} fontSize={FONTE_ROTULO} fontWeight={600}>
        {formatar(v, index)}
      </text>
    );
  };
  return RotuloDoPonto;
}

function lugarDoTexto(x: number, y: number, indice: number, posicao: PosicaoPonto) {
  if (posicao.aDireitaNo === indice) return { x: x + 8, y, textAnchor: 'start', dominantBaseline: 'central' } as const;
  return { x, y: posicao.abaixo ? y + FONTE_ROTULO + 6 : y - 9, textAnchor: posicao.ancora ?? 'middle' } as const;
}

/**
 * Onde o rótulo do ponto fica: acima e centrado (padrão); `ancora: 'end'` termina o texto no
 * ponto (o último dia, na borda direita, não sai do gráfico); `abaixo` desce o texto para não
 * bater no rótulo de outra linha que termina no mesmo dia; `aDireitaNo` põe um índice à direita.
 */
export interface PosicaoPonto {
  ancora?: 'middle' | 'end';
  abaixo?: boolean;
  /**
   * Índice cujo rótulo vai à direita do ponto, na altura dele (o último mês de várias linhas
   * próximas: em cima, o número de uma cai na linha de baixo). Pede margem direita.
   */
  aDireitaNo?: number;
  /** Número girado 90°, subindo a partir do ponto (colunas estreitas demais para o número deitado). */
  emPe?: boolean;
}
